import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { StarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import type { FullPostType } from 'src/app/pages/starterDashboardV2/shared/components';
import { extractSharingResource } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type { MappedSharingResource } from 'src/app/pages/starterDashboardV2/shared/contexts';
import type {
	FacebookShareRecommendations,
	RedditShareRecommendations,
	Template,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import {
	useFlyerTemplates,
	useReadyToShare,
	useShareRecommendations,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import type { ShareVisualImg } from 'src/app/pages/starterDashboardV2/shared/types';

import { useShareVisual } from '../../../GrowPetition/components/ShareVisual/hooks/useShareVisual';

import type { RecommendedGroupWithPlatform } from './types';

type SharingToolsTrackingContext = 'petition_dashboard_grow_petition_v1' | 'petition_dashboard_grow_petition_v2';

type SharingToolsData = {
	recommendedGroups: {
		isLoading: boolean;
		groups: RecommendedGroupWithPlatform[];
	};
	readyToSharePosts: {
		isLoading: boolean;
		facebookPost?: SharingPostData;
		redditPost?: SharingPostData;
		instagramStory?: SharingPostData;
	};
	shareVisual: {
		isLoading: boolean;
		image?: {
			url: string;
			variationName: string;
		};
	};
	posters: {
		isLoading: boolean;
		template?: {
			name: string;
			images: Template['images'];
		};
	};
};

type SharingToolsActions = {
	navigateToAllTools: () => void;
	navigateToGroupRecommendations: () => void;
	navigateToReadyToSharePosts: () => void;
	navigateToShareVisual: () => void;
	navigateToPosters: () => void;
	navigateToEditPetition: () => void;
};

export type UseSharingToolsReturn = ModelHookResult<SharingToolsData, SharingToolsActions>;

export type SharingPostData = {
	fullPostType: FullPostType;
	sharingResource: MappedSharingResource;
};

function buildRecommendedGroupsArray(
	reddit: RedditShareRecommendations,
	facebook: FacebookShareRecommendations,
): RecommendedGroupWithPlatform[] {
	const result: RecommendedGroupWithPlatform[] = [];

	// If there is at least one reddit group, that should be the first element
	if (reddit.length > 0) {
		result.push({ group: reddit[0], platform: 'reddit' });

		// If there are reddit and facebook group recommendations, return the first reddit group, and the first facebook group
		if (facebook.length > 0) {
			result.push({ group: facebook[0], platform: 'facebook' });
		}
		// If there's more than 1 reddit recommendation, but no facebook recommendations, return the 2 first reddit recommendations
		else if (reddit.length > 1) {
			result.push({ group: reddit[1], platform: 'reddit' });
		}
	}
	// If there are no reddit recommendations, but there are facebook recommendations, use facebook instead
	else if (facebook.length > 0) {
		result.push({ group: facebook[0], platform: 'facebook' });
		if (facebook.length > 1) {
			result.push({ group: facebook[1], platform: 'facebook' });
		}
	}

	return result;
}

function mapReadyToSharePosts(
	petition: StarterDashboardV2PageData['petition'],
	readyToSharePosts: ReturnType<typeof useReadyToShare>['data']['platformData'],
): Omit<SharingToolsData['readyToSharePosts'], 'isLoading'> {
	const facebookPost = readyToSharePosts.FACEBOOK?.items.find((post) => post.postTypeName === 'post');
	const redditPost = readyToSharePosts.REDDIT?.items.find((post) => post.postTypeName === 'post');
	const instagramStory = readyToSharePosts.INSTAGRAM?.items.find((post) => post.postTypeName === 'story');
	return {
		facebookPost: facebookPost
			? {
					fullPostType: 'facebook_post',
					sharingResource: extractSharingResource('facebook_post', {
						post: facebookPost,
						petition,
						recommendedGroup: undefined,
					}),
				}
			: undefined,
		redditPost: redditPost
			? {
					fullPostType: 'reddit_post',
					sharingResource: extractSharingResource('reddit_post', {
						post: redditPost,
						petition,
						recommendedGroup: undefined,
					}),
				}
			: undefined,
		instagramStory: instagramStory
			? {
					fullPostType: 'instagram_story',
					sharingResource: extractSharingResource('instagram_story', {
						post: instagramStory,
						petition,
						recommendedGroup: undefined,
					}),
				}
			: undefined,
	};
}

function mapShareVisualImage(shareVisualImages?: ShareVisualImg[], imageVariantName = 'post_variant_c') {
	const bestOption = shareVisualImages?.find((image) => image.variantName === imageVariantName);
	if (bestOption) {
		return { url: bestOption.image.src, variationName: bestOption.variantName };
	}
	const shareVisualPostImage = shareVisualImages?.find((image) => image.variantName.startsWith('post'));
	if (shareVisualPostImage) {
		return { url: shareVisualPostImage.image.src, variationName: shareVisualPostImage.variantName };
	}
	return undefined;
}

function findFlyerTemplate(flyerTemplates: Template[]) {
	return (
		flyerTemplates.find(({ name, enabled }) => name === 'flyer-b' && enabled) ??
		flyerTemplates.find(({ enabled }) => enabled) ??
		undefined
	);
}

type BuildSharingToolsDataParams = {
	recommendedGroupsData: {
		shareRecommendations: { reddit: RedditShareRecommendations; facebook: FacebookShareRecommendations };
		shareRecommendationsLoading: boolean;
	};
	readyToSharePostsData: {
		isLoading: boolean;
		platformData: ReturnType<typeof useReadyToShare>['data']['platformData'];
	};
	shareVisualData: { shareVisualsState: string; items?: ShareVisualImg[] };
	flyerTemplates: Template[];
	petition: StarterDashboardV2PageData['petition'];
	options?: { imageVariantName?: string };
};

function buildSharingToolsData(params: BuildSharingToolsDataParams): SharingToolsData {
	const { recommendedGroupsData, readyToSharePostsData, shareVisualData, flyerTemplates, petition, options } = params;
	const mappedReadyToSharePosts = mapReadyToSharePosts(petition, readyToSharePostsData.platformData);
	const recommendedGroupsArray = buildRecommendedGroupsArray(
		recommendedGroupsData.shareRecommendations.reddit,
		recommendedGroupsData.shareRecommendations.facebook,
	);
	const shareVisualImage = mapShareVisualImage(shareVisualData.items, options?.imageVariantName);
	const template = findFlyerTemplate(flyerTemplates);

	return {
		recommendedGroups: {
			isLoading: recommendedGroupsData.shareRecommendationsLoading,
			groups: recommendedGroupsArray,
		},
		readyToSharePosts: {
			isLoading: readyToSharePostsData.isLoading,
			...mappedReadyToSharePosts,
		},
		shareVisual: {
			isLoading: shareVisualData.shareVisualsState === 'loading',
			image: shareVisualImage,
		},
		posters: {
			isLoading: false,
			template: template ? { name: template.name, images: template.images } : undefined,
		},
	};
}

const CLICK_EVENT_NAME = 'petition_manage_grow_your_petition_click';

function navigateToGroupRecommendations(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
) {
	void track(CLICK_EVENT_NAME, {
		context: trackingContext,
		content: 'recommended_groups',
		cta: 'button',
	});
	navigate(`/p/${slug}/dashboard/share#recommendations-title`);
}

function navigateToShareVisual(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
) {
	void track(CLICK_EVENT_NAME, {
		context: trackingContext,
		content: 'social_content',
		cta: 'button',
	});
	navigate(`/p/${slug}/dashboard/share#social-media-content-container`);
}

function navigateToPosters(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
) {
	void track(CLICK_EVENT_NAME, {
		context: trackingContext,
		content: 'poster',
		cta: 'button',
	});
	navigate(`/p/${slug}/dashboard/share#share_qrcode_module`);
}

function navigateToEditPetition(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
) {
	void track(CLICK_EVENT_NAME, {
		context: trackingContext,
		content: 'recommended_groups',
		cta: 'button',
	});
	navigate(`/p/${encodeURIComponent(slug)}/edit`);
}

function navigateToAllTools(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
) {
	void track(CLICK_EVENT_NAME, {
		context: trackingContext,
		content: 'all',
		cta: 'link',
	});
	navigate(`/p/${slug}/dashboard/share`);
}

function navigateToReadyToSharePosts(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	track: ReturnType<typeof useTracking>,
	trackingContext: SharingToolsTrackingContext,
) {
	void track(CLICK_EVENT_NAME, {
		context: trackingContext,
		content: 'rts_posts',
		cta: 'button',
	});
	navigate(`/p/${slug}/dashboard/share#ready-to-share-posts`);
}

export function useSharingTools(
	trackingContext: SharingToolsTrackingContext = 'petition_dashboard_grow_petition_v2',
	options?: { imageVariantName?: string },
): UseSharingToolsReturn {
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();
	const recommendedGroups = useShareRecommendations(petition.slug);
	const readyToSharePosts = useReadyToShare();
	const shareVisual = useShareVisual();
	const flyerTemplates = useFlyerTemplates({ petition, context: 'starter_dashboard_sharing_tools' });
	const navigate = useNavigate();
	const track = useTracking();

	const data = buildSharingToolsData({
		recommendedGroupsData: recommendedGroups.data,
		readyToSharePostsData: readyToSharePosts.data,
		shareVisualData: shareVisual.data,
		flyerTemplates,
		petition,
		options,
	});

	return {
		data,
		actions: {
			navigateToGroupRecommendations: () =>
				navigateToGroupRecommendations(petition.slug, navigate, track, trackingContext),
			navigateToAllTools: () => navigateToAllTools(petition.slug, navigate, track, trackingContext),
			navigateToReadyToSharePosts: () => {
				navigateToReadyToSharePosts(petition.slug, navigate, track, trackingContext);
			},
			navigateToEditPetition: () => navigateToEditPetition(petition.slug, navigate, track, trackingContext),
			navigateToShareVisual: () => navigateToShareVisual(petition.slug, navigate, track, trackingContext),
			navigateToPosters: () => navigateToPosters(petition.slug, navigate, track, trackingContext),
		},
	};
}
